import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Divider, CircularProgress,
  Stack, Avatar, Tooltip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CallIcon from '@mui/icons-material/Call';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { salesExecutiveApi } from '../api/apiClient';
import PageShell from '../common/PageShell';

const colorFor = (name = '') => {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5'];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return palette[sum % palette.length];
};

const InfoRow = ({ icon, label, value, color = 'primary.main' }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
    <Avatar
      sx={{
        bgcolor: color,
        width: 36,
        height: 36,
        flexShrink: 0,
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word', mt: 0.25 }}>
        {value || '—'}
      </Typography>
    </Box>
  </Stack>
);

const SalesExecutiveView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    salesExecutiveApi.getById(id).then((res) => setData(res.data));
  }, [id]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const fullName = `${data.firstName || ''} ${data.middleName || ''} ${data.lastName || ''}`.trim() || '—';
  const initials = `${(data.firstName || '').charAt(0)}${(data.lastName || '').charAt(0)}`.toUpperCase() || '?';
  const color = colorFor(fullName);
  const addr = data.address || {};
  const hasAddress = [
    addr.addressLine1,
    addr.addressLine2,
    addr.city,
    addr.state,
    addr.country,
    addr.pincode,
  ].some(Boolean);
  const addressString = [
    addr.addressLine1,
    addr.addressLine2,
    addr.city,
    addr.state,
    addr.country,
    addr.pincode,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <PageShell
      icon={<BadgeIcon />}
      title={fullName}
      subtitle="Sales executive profile"
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Sales Executives', to: '/sales-executives' },
        { label: fullName },
      ]}
      actions={
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/sales-executives')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/sales-executives/edit/${id}`)}
            sx={{
              bgcolor: '#fff',
              color: '#1e3a8a',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            Edit
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {/* LEFT: main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Profile summary */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ mb: 2.5 }}
              >
                <Avatar
                  sx={{
                    bgcolor: color,
                    width: 72,
                    height: 72,
                    fontSize: 26,
                    fontWeight: 700,
                    boxShadow: `0 8px 20px ${color}44`,
                  }}
                >
                  {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}
                    >
                      {fullName}
                    </Typography>
                    <Chip
                      label={data.active ? 'Active' : 'Inactive'}
                      color={data.active ? 'success' : 'default'}
                      size="small"
                      icon={data.active ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Executive ID #{data.id}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<PhoneIcon />}
                    label="Mobile Number"
                    value={data.mobileNumber}
                    color="#2563eb"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<WorkHistoryIcon />}
                    label="Experience"
                    value={data.experience ? `${data.experience} years` : '—'}
                    color="#7c3aed"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Address */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 36,
                    height: 36,
                    '& svg': { fontSize: 18 },
                  }}
                >
                  <HomeIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Address
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hasAddress ? 'Contact address on record' : 'No address on record'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {hasAddress ? (
                <Grid container spacing={2}>
                  {addr.addressLine1 && (
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        icon={<HomeIcon />}
                        label="Address Line 1"
                        value={addr.addressLine1}
                        color="#db2777"
                      />
                    </Grid>
                  )}
                  {addr.addressLine2 && (
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        icon={<HomeIcon />}
                        label="Address Line 2"
                        value={addr.addressLine2}
                        color="#db2777"
                      />
                    </Grid>
                  )}
                  {addr.city && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoRow icon={<LocationOnIcon />} label="City" value={addr.city} color="#0891b2" />
                    </Grid>
                  )}
                  {addr.state && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoRow icon={<LocationOnIcon />} label="State" value={addr.state} color="#0891b2" />
                    </Grid>
                  )}
                  {addr.country && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoRow
                        icon={<LocationOnIcon />}
                        label="Country"
                        value={addr.country}
                        color="#0891b2"
                      />
                    </Grid>
                  )}
                  {addr.pincode && (
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoRow
                        icon={<LocationOnIcon />}
                        label="Pincode"
                        value={addr.pincode}
                        color="#0891b2"
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This sales executive has no address on file.
                </Typography>
              )}
            </Paper>
          </Stack>
        </Grid>

        {/* RIGHT: sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3} sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            {/* At a glance */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                At a Glance
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={data.active ? 'Active' : 'Inactive'}
                    color={data.active ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Experience
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {data.experience ? `${data.experience} yrs` : '—'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Mobile
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
                    {data.mobileNumber || '—'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Record ID
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    #{data.id}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Quick actions */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                Quick Actions
              </Typography>
              <Stack spacing={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/sales-executives/edit/${id}`)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Edit Executive
                </Button>
                {data.mobileNumber && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CallIcon />}
                    href={`tel:${data.mobileNumber}`}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Call Executive
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<StorefrontIcon />}
                  onClick={() => navigate('/marketing')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  View Marketing
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/sales-executives')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Back to List
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default SalesExecutiveView;